import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { InputAdornment, TextField } from "@mui/material";
import React, { useState } from "react";
import ImgSliderBox from "@/shared/components/common/sliderControl";
import { useMutation, useQuery } from "@tanstack/react-query";
import {fetchWalletBalance,postPaymentCharge,} from "@/api/user/home";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import CardBoxLayout from "@/modules/user/layout/cardBoxLayout";
import { numberSeparator, toEnglishNumber, toPersianNumber,} from "@/hooks/useThousandsSeparator";
import { PulseLoader } from "react-spinners";
/**
 * ContentCashBalanceRecharge
 *
 * کامپوننت شارژ کیف پول اعتباری کاربر.
 *
 * ویژگی‌ها:
 * - نمایش موجودی فعلی کیف پول
 * - ورود مبلغ شارژ با فرمت عددی فارسی
 * - انتخاب درگاه پرداخت از بین چند گزینه
 * - نمایش پیش‌نمایش درگاه انتخاب شده
 * - مدیریت ارسال درخواست پرداخت با React Query و نمایش لودر
 *
 * @returns JSX.Element
 */
// Task Response:

// بهینه‌سازی‌ها و نکات احتمالی:
// 1. handleChangeState تابعی تعریف شده اما استفاده نشده، می‌توانید از آن در TextField استفاده کنید.
// 2. numberSeparator و toPersianNumber به خوبی برای فرمت عدد استفاده شده‌اند.
// 3. محدودیت مبلغ 5,000,000 ریال اعمال شده، اگر سقف قابل تغییر است بهتر است متغیر باشد.
// 4. isPending در mutation استفاده شده برای نمایش PulseLoader، درست است.
// 5. window.open برای پرداخت مستقیم درست است اما بهتر است target="_blank" و noreferrer برای امنیت اضافه شود.
// 6. می‌توانید useEffect برای initial selectedImage اضافه کنید تا همیشه مقدار پیش‌فرض درست تنظیم شود.
// 7. کلاس‌های Tailwind با sx مختلط شده‌اند؛ برای هماهنگی بهتر، یکی را انتخاب کنید.
// 8. step و handleNextStep تعریف شده اما در این کامپوننت استفاده نشده‌اند؛ می‌توان حذف کرد یا برای مراحل بعدی استفاده کرد.

const ContentCashBalanceRecharge = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["wallet-balance"],
    queryFn: fetchWalletBalance,
  });

  const {mutate: fetchPaymentCharge,data: postPaymentChargeData,error: errorPaymentCharge,isPending,} = useMutation({
    mutationFn: postPaymentCharge,
    onSuccess: (data) => {
      window.open(data.data.url, "_blank");
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const [selectedImage, setSelectedImage] = useState<any>({ id: 1, title: "fanava", img: "/fanavar.png", name: "فن آوا" });

  const [amountState, setAmountState] = useState("");
  const arrayImage = [
    { id: 1, title: "fanava", img: "/fanavar.png", name: "فن آوا" },
    // { id: 2, title: "aap", img: "/aap.png", name: "آپ" },
    // { id: 3, title: "vandar", img: "/vandar2.png", name: "وندار" },
    // { id: 4, title: "pariz", img: "/pariz.png", name: "پاریزپی" },
  ];

  const handleNextStep = (id: number) => {
    setStep(id);
  };

  const handleChangeState = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
      .replace(/[^\d۰-۹]/g, "")
      .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)));
    setAmountState(raw);
  };

  const onSubmitContinue = () => {
    const requestBody = {
      gateway: `${selectedImage?.title}`,
      amount: amountState,
    };
    fetchPaymentCharge(requestBody);
  };

  const [step, setStep] = React.useState(0);

  return (
    <StackKit direction="column" className="w-full items-center justify-center" spacing={3} >
      <CardBoxLayout className="w-full flex flex-col gap-5 " sx={{padding:'1rem'}} >
        <BoxKit className="w-full flex flex-col sm:flex-row sm:justify-between items-start sm:items-center mb-4">
          <TypoGraphyKit variant="body1" className="text-base sm:text-lg">
            شما در حال حاضر در فرآیند شارژ کیف پول اعتباری به شماره{" "}
            {toPersianNumber(`${data?.cards[0]?.number}`)} هستید.
          </TypoGraphyKit>
        </BoxKit>
        <StackKit direction={{ xs: "column", sm: "row" }} sx={{ width: "100%" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} mb={4}>
          <TypoGraphyKit variant="body1">مبلغ مورد نظر خود را وارد نمایید</TypoGraphyKit>
          <TextField
            sx={{ width: { xs: "100%", sm: "50%" } }}
            placeholder={`${numberSeparator(200000000)}`}
            onChange={(e) => {
              const englishNumber = toEnglishNumber(e.target.value);
              const rawValue = englishNumber.replace(/[^0-9]/g, "");
              if (Number(rawValue) <= 200000000) setAmountState(rawValue);
            }}
            value={amountState ? numberSeparator(amountState) : ""}
            InputProps={{endAdornment: <InputAdornment position="end">ریال</InputAdornment>,}}
          />
        </StackKit>
        <StackKit direction={"column"} justifyContent={'start'} sx={{ width: "100%" }} spacing={2} mb={4}>
          <TypoGraphyKit className="text-right w-full" variant="body1">
            در صورت تمایل درگاه مورد نظر خود را انتخاب نمایید
          </TypoGraphyKit>
          <BoxKit className="flex w-full justify-end">
            <ImgSliderBox className="w-full max-w-[400px]" imageList={arrayImage} setSelectedImage={setSelectedImage}/>
          </BoxKit>
        </StackKit>
        <StackKit direction={"column"} sx={{ width: "100%", color: "#666666" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={2}>
          <StackKit direction={{ xs: "column", sm: "row" }} justifyContent="flex-start" alignItems="center" spacing={2} className="w-full text-right pb-3" gap={2}>
            <StackKit direction={{sm:'row',xs:'column'}} gap={2} justifyContent="flex-start" alignItems="center" className="w-full" >
              <TypoGraphyKit variant="body1">
                درگاه پیش فرض: {selectedImage.name}
              </TypoGraphyKit>
              {selectedImage?.img && (
                <BoxKit className="w-[150px] h-[100px] overflow-hidden bg-white rounded">
                  <img src={selectedImage.img} className="w-full h-full rounded object-cover" alt="imgSelect"/>
                </BoxKit>
              )}
            </StackKit>
            <CustomButton className="w-full max-w-[250px] h-[48px]" onClick={onSubmitContinue}>
              {isPending ? (
                <StackKit sx={{ padding: "5px" }}>
                  <PulseLoader color="#fff" size={5} speedMultiplier={1} />
                </StackKit>
              ) : (
                "ادامه"
              )}
            </CustomButton>
          </StackKit>
          <TypoGraphyKit className="text-right w-full " color="#666" marginTop={10} variant="body1">
            سقف واریز روزانه: {numberSeparator(200000000)} ریال
          </TypoGraphyKit>
        </StackKit>
      </CardBoxLayout>
    </StackKit>

  );
};

export default ContentCashBalanceRecharge;
